import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthGaurdService } from 'src/app/service/auth-gaurd.service';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import { Router } from '@angular/router';
import { ExportExcelService } from 'src/app/service/export-excel.service';
import { UploadsConfigModel } from 'src/app/models/upload/uploadsconfig.model';
import { UploadsConfigService } from 'src/app/service/uploadsconfig.service';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { GlobalService } from 'src/app/service/global.service';
import {
  dedupeAndSortByTemplateName,
  extractApiErrorMessage,
  normalizeUploadFilterValue,
  parseExcelFileToBodyRows
} from '../upload-template.util';

@Component({
  selector: 'app-upload-data',
  templateUrl: './upload-data.component.html',
  styleUrls: ['./upload-data.component.css']
})
export class UploadDataComponent implements OnInit, OnDestroy {

  public uploadsConfigModel = new UploadsConfigModel();
  public dropDownsData: UploadsConfigModel[] = [];
  public filteredTemplatesData: UploadsConfigModel[] = [];
  public loggedinUser = '';
  descValue: string = null;
  selectedActionType = 'Insert';

  bulkData: any[][] = [];
  selectedRecordTemplate = '';

  filteredTemplateValues!: Observable<UploadsConfigModel[]>;
  controlSc = new FormControl();
  hideDiv = false;
  hideMsg = true;
  isAccessDisabled = false;
  uploading = false;
  uploadProgress = 0;
  uploadStatusMessage = '';

  successCount = 0;
  failureCount = 0;
  rowCount = 0;
  failedRows = '';

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  private subscriptions: Subscription[] = [];
  private processingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private globalService: GlobalService,
    private router: Router,
    private uploadsConfigService: UploadsConfigService,
    public ete: ExportExcelService,
    private authGaurdService: AuthGaurdService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.isAccessDisabled = this.globalService._permissionCheck('upload-data');
    if (this.isAccessDisabled) {
      this.controlSc.disable();
    }
    this.loggedinUser = this.authGaurdService.loginUser();
    this.getTemplatesDropdown();
  }

  ngOnDestroy(): void {
    this.stopProcessingTicker();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  public getTemplatesDropdown(): void {
    this.subscriptions.push(
      this.uploadsConfigService.getRecordTemplatesList().subscribe(
        (response: UploadsConfigModel[]) => {
          this.dropDownsData = response;
          this.filteredTemplatesData = dedupeAndSortByTemplateName(response);
          this.filterTemplates();
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  public redirectPage(pageName: string): void {
    this.router.navigate(['/' + pageName]);
  }

  filterTemplates(): void {
    this.filteredTemplateValues = this.controlSc.valueChanges.pipe(
      startWith(''),
      map(value => this._filterSC(value))
    );
  }

  private _filterSC(value: string): UploadsConfigModel[] {
    const filterValue = normalizeUploadFilterValue(value);
    return this.filteredTemplatesData.filter(item =>
      normalizeUploadFilterValue(item.uploadtemplatename).includes(filterValue)
    );
  }

  public getSelectedTemplates(val: string): string {
    if (!val) {
      return '';
    }
    const match = this.filteredTemplatesData.find(item => item.recordtemplate === val);
    return match ? match.uploadtemplatename : '';
  }

  public getSelectedSc(event: { option: { value: string } }): void {
    this.selectedRecordTemplate = event.option.value;
    this.hideDiv = true;
    this.hideMsg = false;
    const selected = this.filteredTemplatesData.find(item => item.recordtemplate === event.option.value);
    this.descValue = selected ? selected.uploadtemplatedesc : null;
    this.clearSelectedFile();
  }

  public onActionTypeChange(actionType: string): void {
    this.selectedActionType = actionType || 'Insert';
    this.clearSelectedFile();
  }

  downloadExcelHeaders(): void {
    if (!this.selectedRecordTemplate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select a record template');
      return;
    }
    if (!this.selectedActionType) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Insert or Update');
      return;
    }
    const fileTitle = this.selectedActionType === 'Update' ? 'Template_Update' : 'Template_Insert';
    this.subscriptions.push(
      this.uploadsConfigService.getRecordsListForAction(this.selectedRecordTemplate, this.selectedActionType).subscribe(
        (response: UploadsConfigModel[]) => {
          this.ete.exportHeaderOnlyTemplate(fileTitle, response);
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  onFileChange(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.bulkData = [];
      return;
    }
    parseExcelFileToBodyRows(file)
      .then(rows => {
        this.bulkData = rows;
      })
      .catch(() => {
        this.bulkData = [];
        this.notificationService.sendNotification(NotificationType.ERROR, 'Unable to read the selected file');
      });
  }

  uploadFile(): void {
    if (!this.uploadsConfigModel.recordtemplate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select operation');
      return;
    }
    if (!this.selectedActionType) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Insert or Update');
      return;
    }
    if (!this.bulkData.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please Add valid data');
      return;
    }

    const recordTemplate = this.uploadsConfigModel.recordtemplate;
    const actionType = this.selectedActionType;

    this.uploading = true;
    this.uploadProgress = 0;
    this.uploadStatusMessage = `Loading ${actionType} SQL...`;
    this.stopProcessingTicker();

    this.subscriptions.push(
      this.uploadsConfigService.getSqlQueryForAction(recordTemplate, actionType).subscribe(
        sqlResponse => {
          if (!(sqlResponse?.sqlquery || '').trim()) {
            this.uploading = false;
            this.uploadStatusMessage = '';
            this.notificationService.sendNotification(
              NotificationType.ERROR,
              `No ${actionType} SQL configured for the selected template`
            );
            return;
          }
          this.uploadStatusMessage = 'Preparing upload...';
          this.startUpload(recordTemplate, actionType);
        },
        (errorResponse: HttpErrorResponse) => {
          this.uploading = false;
          this.uploadProgress = 0;
          this.uploadStatusMessage = '';
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  private startUpload(recordTemplate: string, actionType: string): void {
    this.subscriptions.push(
      this.uploadsConfigService.uploadExcelWithProgress(
        this.bulkData,
        recordTemplate,
        actionType
      ).subscribe({
        next: event => {
          if (event.type === 'progress') {
            const scaledProgress = Math.min(85, Math.round(event.progress * 0.85));
            this.uploadProgress = Math.max(this.uploadProgress, scaledProgress);
            this.uploadStatusMessage = event.uploadComplete
              ? `Processing rows with ${actionType} SQL...`
              : 'Uploading data...';
            if (event.uploadComplete) {
              this.startProcessingTicker();
            }
            return;
          }

          this.stopProcessingTicker();
          this.uploadProgress = 100;
          this.uploadStatusMessage = 'Upload complete';
          const data = event.body;
          this.successCount = data.successCount;
          this.failureCount = data.failureCount;
          this.rowCount = data.rowCount;
          this.failedRows = data.failedRows || '';
          this.bulkData = [];
          this.uploadsConfigModel.recordtemplate = null;
          this.selectedRecordTemplate = '';
          this.selectedActionType = 'Insert';
          this.controlSc.setValue('');
          this.descValue = null;
          this.clearSelectedFile();
          setTimeout(() => {
            this.uploading = false;
            this.uploadProgress = 0;
            this.uploadStatusMessage = '';
          }, 600);
        },
        error: () => {
          this.stopProcessingTicker();
          this.uploading = false;
          this.uploadProgress = 0;
          this.uploadStatusMessage = '';
          this.notificationService.sendNotification(NotificationType.ERROR, 'Upload Failed');
        }
      })
    );
  }

  private clearSelectedFile(): void {
    this.bulkData = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private startProcessingTicker(): void {
    if (this.processingInterval) {
      return;
    }
    this.processingInterval = setInterval(() => {
      if (this.uploadProgress < 98) {
        this.uploadProgress += 1;
      }
    }, 350);
  }

  private stopProcessingTicker(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
  }

  downloadTextFile(): void {
    const blob = new Blob([this.failedRows], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'failedData.txt';
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  trackByRecordTemplate(_index: number, option: UploadsConfigModel): string {
    return option.recordtemplate;
  }
}
